import { useEffect, useMemo } from 'react'

import { useCADQuery } from '@/services/queries/cads'
import { useOperationSteps } from '@/services/queries/operation_steps'
import {
  countAllParts,
  findPartsInOperation,
} from '../fns/findPartsInOperation'

import type { BillOfMaterials, ToolsBillOfMaterials } from '@/state'
import {
  partsTablesQuery,
  toolsTablesQuery,
} from '@/services/hooks/template_attributes'
import { MUTATION_KEYS as CAD_VERSIONS_MUTATION_KEYS } from '@/services/queries/cad_versions'
import { useMutationState, useQuery } from '@tanstack/react-query'
import queryClient from '@/queryClient'

export const useStepBOM = ({
  documentPageId,
}: {
  documentPageId?: string | null
}): BillOfMaterials => {
  const { data: cadData } = useCADQuery()

  const { isLoading: isLoadingSteps, steps } = useOperationSteps()
  const assemblyTree = useMemo(
    () => cadData.rawAssemblyTree,
    [cadData.rawAssemblyTree],
  )

  const stepBom = useMemo(() => {
    if (!assemblyTree || isLoadingSteps || !steps || !documentPageId) {
      return []
    }

    if (!assemblyTree.nodes) {
      return []
    }

    const step = steps.find((step) => step.id === documentPageId)

    if (!step) {
      console.warn(
        'Unable to find parts in operation: Operation step not found',
      )
      return []
    }

    return findPartsInOperation(step, assemblyTree)
  }, [assemblyTree, isLoadingSteps, documentPageId, steps])

  return stepBom
}

export const useFullPartsBOM = (): BillOfMaterials => {
  const { data: cadData } = useCADQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const assemblyTree = useMemo(() => cadData?.rawAssemblyTree, [cadData])

  const customPartsQuery = useQuery({
    queryKey: ['custom_parts'],
    enabled: !isLoadingSteps && !!steps?.length,
    queryFn: async () => {
      const partsTables = await Promise.all(
        steps.reduce(
          (accum, step) => accum.concat(partsTablesQuery(step.id)),
          [] as any,
        ),
      ).then((res) => res.flat())

      const customParts = partsTables.map(
        (table) => table?.template_values.rows || [],
      )

      return customParts?.reduce((accum, pageCustomParts) => {
        pageCustomParts.forEach((part) => {
          const { partName, quantity } = part
          if (partName) {
            const finalQuantity =
              !quantity || isNaN(Number(quantity)) ? 0 : Number(quantity)
            if (accum[partName]) {
              accum[partName] += finalQuantity
            } else {
              accum[partName] = finalQuantity
            }
          }
        })
        return accum
      }, {} as any)
    },
  })

  const tableMutationData = useMutationState({
    filters: {
      status: 'success',
      predicate: (mutation) => {
        const [mutationKey] = mutation.options.mutationKey || []
        const tableType = (mutation.state.variables as any).payload
          ?.template_values?.type

        if (!mutationKey) return false

        return (
          mutationKey === CAD_VERSIONS_MUTATION_KEYS.UPDATE_CAD_VERSION ||
          tableType === 'parts-table'
        )
      },
    },
  })

  useEffect(() => {
    const invalidateQueries = async () =>
      queryClient.invalidateQueries({
        queryKey: ['custom_parts'],
      })

    invalidateQueries().catch((error) => {
      console.error(error)
    })
  }, [tableMutationData])

  if (!assemblyTree || isLoadingSteps || !steps) return []

  return countAllParts({ assemblyTree, customParts: customPartsQuery.data })
}

export const useFullToolsBOM = (): ToolsBillOfMaterials => {
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const customToolsQuery = useQuery({
    queryKey: ['custom_tools'],
    enabled: !isLoadingSteps && !!steps?.length,
    queryFn: async () => {
      const toolsTables = await Promise.all(
        steps.reduce(
          (accum, step) => accum.concat(toolsTablesQuery(step.id)),
          [] as any,
        ),
      ).then((res) => res.flat())

      const customTools = toolsTables.map(
        (table) => table?.template_values.rows || [],
      )

      return customTools?.reduce((accum, pageCustomParts) => {
        pageCustomParts.forEach((tool) => {
          const { toolName } = tool
          const childInBom = accum.find((bom) => bom.toolName === toolName)
          if (!childInBom) {
            accum.push({ toolName })
          }
        })
        return accum
      }, [] as any)
    },
  })

  const tableMutationData = useMutationState({
    filters: {
      status: 'success',
      predicate: (mutation) => {
        const [mutationKey] = mutation.options.mutationKey || []
        const tableType = (mutation.state.variables as any).payload
          ?.template_values?.type

        if (!mutationKey) return false

        return (
          mutationKey === CAD_VERSIONS_MUTATION_KEYS.UPDATE_CAD_VERSION ||
          tableType === 'tools-table'
        )
      },
    },
  })

  useEffect(() => {
    const invalidateQueries = async () => {
      await queryClient.invalidateQueries({
        queryKey: ['custom_tools'],
      })
    }

    invalidateQueries().catch((error) => {
      console.error(error)
    })
  }, [tableMutationData])

  if (isLoadingSteps || !steps) return []

  return customToolsQuery.data || []
}
